import { Injectable } from '@angular/core';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  CardapioConsultaDTO,
  CardapioPedidoPublicoDTO,
  CardapioPedidoPublicoConsultaDTO,
  CardapioRucConsultaDTO,
  CardapioCobrancaQrDTO,
  CardapioCobrancaStatusDTO,
} from '../../models/cardapio-digital/cardapio-digital.model';

/**
 * Servico para chamadas publicas do cardapio digital.
 * Usa HttpBackend para criar um HttpClient que bypassa TODOS os interceptors,
 * evitando que o AuthInterceptor injete token JWT ou redirecione para /login.
 */
@Injectable({ providedIn: 'root' })
export class CardapioPublicoService {
  private http: HttpClient;
  private baseUrl = environment.apiBaseUrl;

  constructor(handler: HttpBackend) {
    this.http = new HttpClient(handler);
  }

  /**
   * Busca o cardapio completo de um estabelecimento pelo slug.
   * Endpoint publico: GET /api/cardapio/{slug}
   */
  buscarCardapioPorSlug(slug: string): Observable<CardapioConsultaDTO> {
    return this.http.get<CardapioConsultaDTO>(`${this.baseUrl}/cardapio/${slug}`);
  }

  /**
   * Calcula o frete de entrega por coordenadas (mesmo valor que o backend
   * cobrará ao criar o pedido). Endpoint publico:
   * GET /cardapio/{slug}/calculo-frete?latitude=..&longitude=..
   *
   * Retorna 422 quando o endereço está fora da área de cobertura — o chamador
   * trata como "sem cobertura" e cai no valor padrão do cardápio.
   */
  calcularFreteEntrega(slug: string, latitude: number, longitude: number): Observable<number> {
    return this.http.get<number>(
      `${this.baseUrl}/cardapio/${slug}/calculo-frete`,
      { params: { latitude: String(latitude), longitude: String(longitude) } }
    );
  }

  /**
   * Cria um pedido via cardapio digital.
   * Endpoint publico: POST /cardapio/{slug}/pedido
   * Usa HttpBackend (sem AuthInterceptor).
   */
  criarPedido(slug: string, dto: CardapioPedidoPublicoDTO): Observable<CardapioPedidoPublicoConsultaDTO> {
    return this.http.post<CardapioPedidoPublicoConsultaDTO>(
      `${this.baseUrl}/cardapio/${slug}/pedido`,
      dto
    );
  }

  /**
   * Gera a cobranca por QR Code do checkout, ANTES de existir pedido.
   * Endpoint publico: POST /cardapio/{slug}/cobranca-qr
   *
   * Recebe o mesmo payload do pedido: e exatamente ele que fica guardado no backend a espera da
   * confirmacao — o pedido so nasce quando a Bancard confirmar o pagamento.
   */
  criarCobrancaQr(slug: string, dto: CardapioPedidoPublicoDTO): Observable<CardapioCobrancaQrDTO> {
    return this.http.post<CardapioCobrancaQrDTO>(
      `${this.baseUrl}/cardapio/${slug}/cobranca-qr`,
      dto
    );
  }

  /**
   * Acompanha a cobranca enquanto o cliente paga.
   * Endpoint publico: GET /cardapio/{slug}/cobranca-qr/{intencaoId}
   */
  consultarCobrancaQr(slug: string, intencaoId: number): Observable<CardapioCobrancaStatusDTO> {
    return this.http.get<CardapioCobrancaStatusDTO>(
      `${this.baseUrl}/cardapio/${slug}/cobranca-qr/${intencaoId}`
    );
  }

  /**
   * Cancela a cobranca quando o cliente desiste dela.
   * Endpoint publico: POST /cardapio/{slug}/cobranca-qr/{intencaoId}/cancelar
   *
   * Trocar a forma de pagamento (ou sair do checkout) nao invalida sozinho o QR ja exibido: ele
   * continua pagavel na Bancard ate a cobranca ser cancelada aqui.
   */
  cancelarCobrancaQr(slug: string, intencaoId: number): Observable<CardapioCobrancaStatusDTO> {
    return this.http.post<CardapioCobrancaStatusDTO>(
      `${this.baseUrl}/cardapio/${slug}/cobranca-qr/${intencaoId}/cancelar`,
      {}
    );
  }

  /**
   * Consulta o status de um pedido pelo codigo.
   * Endpoint publico: GET /cardapio/{slug}/pedido/{codigoDePedido}
   * Preparado para uso futuro no tracking.
   */
  consultarStatusPedido(slug: string, codigoDePedido: number): Observable<CardapioPedidoPublicoConsultaDTO> {
    return this.http.get<CardapioPedidoPublicoConsultaDTO>(
      `${this.baseUrl}/cardapio/${slug}/pedido/${codigoDePedido}`
    );
  }

  /**
   * Busca pessoa por RUC ou CI na base interna (e API externa para RUC).
   * Endpoint publico: GET /cardapio/{slug}/buscar-ruc/{valor}?tipo=ruc|ci
   */
  buscarDocumento(slug: string, valor: string, tipo: 'ruc' | 'ci' = 'ruc'): Observable<CardapioRucConsultaDTO> {
    return this.http.get<CardapioRucConsultaDTO>(
      `${this.baseUrl}/cardapio/${slug}/buscar-ruc/${valor}`,
      { params: { tipo } }
    );
  }
}
